import { dojoRecommendationExplanation } from '@/lib/dojoRecommendation';
import { useMemo } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import type {
  DojoImperativeSummary,
  DojoUnitsCatalog,
  DojoUnitSummary,
  GCLDimension,
} from '@/types/dojo';
import { HOWIE_ACTION_CONTROL_CLASS } from '@/components/HowieActionButton';
import { cn } from '@/lib/utils';
import { PracticeDimensionLabel } from './PracticeDimensionLabel';
import { getImperativeOrder, getPracticeTitle } from './imperatives';
import { HOW_DIMENSION_COLOR, HOW_DIMENSION_TITLE } from '@/lib/howDimensions';

const CURRICULUM_DIMENSIONS: GCLDimension[] = ['KNOW', 'BEHAVE', 'RELATE', 'RECOGNIZE', 'PURSUE'];
const SUPPORTING_COPY_CLASS = 'text-center text-sm leading-6 text-how-text-secondary/70';

interface UnitPickerProps {
  catalog: DojoUnitsCatalog | null;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onStartUnit: (unitId: string) => void;
  onReviewRound?: (roundId: string) => void;
  isFirstSession?: boolean;
  disabled?: boolean;
}

interface PracticeCardProps {
  hideDimension?: boolean;
  unit: DojoUnitSummary;
  imperative?: DojoImperativeSummary;
  onStartUnit: (unitId: string) => void;
  onReviewRound?: (roundId: string) => void;
  disabled?: boolean;
}

function getUnitState(unit: DojoUnitSummary): string | null {
  if (unit.status === 'in_progress') return 'In progress';
  return null;
}

function PracticeRow({
  unit,
  imperative,
  onStartUnit,
  onReviewRound,
  disabled = false,
  hideDimension = false,
}: PracticeCardProps) {
  const canReview = Boolean(
    unit.status === 'completed' && unit.latest_round_id && onReviewRound
  );
  const state = getUnitState(unit);
  const action = canReview ? 'Review' : unit.status === 'in_progress' ? 'Continue' : 'Begin';
  const openUnit = () => {
    if (canReview) {
      onReviewRound?.(unit.latest_round_id!);
      return;
    }
    onStartUnit(unit.unit_id);
  };

  return (
    <article className="py-2">
      <button
        type="button"
        onClick={openUnit}
        disabled={disabled}
        title={canReview ? 'Open what you did' : undefined}
        className="group flex w-full flex-col gap-4 rounded-xl px-3 py-5 text-left transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/70 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-row sm:items-center sm:justify-between sm:gap-8"
      >
        <span className="min-w-0">
        {!hideDimension && <PracticeDimensionLabel dimension={unit.primary_dimension} />}
        <span className="block font-body text-[17px] font-medium leading-[22px] tracking-[-0.015em] text-how-space-deep">
          {getPracticeTitle(imperative?.imperative_label)}
        </span>
        <span className="mt-2 block text-[13px] leading-5 text-how-text-secondary/70">
          {unit.title}
        </span>
        </span>
        <span className="flex shrink-0 items-center gap-3 sm:flex-col sm:items-end sm:gap-2">
        {state && (
          <span className="block text-xs leading-4 text-how-text-secondary/70">
            {state}
          </span>
        )}
          <span className="inline-flex min-h-10 min-w-24 items-center justify-center rounded-full border border-how-space-deep/15 bg-white px-4 text-[13px] text-how-space-deep transition-colors group-hover:border-how-space-deep/30">
            {action}
          </span>
        </span>
      </button>

      {canReview && (
        <button
          type="button"
          onClick={() => onStartUnit(unit.unit_id)}
          disabled={disabled}
          aria-label={`Practise ${unit.title} again`}
          className="ml-3 mb-3 inline-flex min-h-10 w-fit items-center gap-1.5 rounded-md text-xs text-how-text-secondary transition-colors hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/25 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RotateCcw size={12} aria-hidden="true" />
          Practice again
        </button>
      )}
    </article>
  );
}

/**
 * The Dojo home separates guidance from choice: one explained suggestion for
 * momentum, and the full dimension-grouped curriculum beneath it.
 */
export function UnitPicker({
  catalog,
  isLoading = false,
  error = null,
  onRetry,
  onStartUnit,
  onReviewRound,
  disabled = false,
}: UnitPickerProps) {
  const imperatives = useMemo(
    () =>
      [...(catalog?.imperatives ?? [])].sort((a, b) => {
        const orderA = a.order ?? getImperativeOrder(a.imperative_id);
        const orderB = b.order ?? getImperativeOrder(b.imperative_id);
        if (orderA !== orderB) return orderA - orderB;
        return getImperativeOrder(a.imperative_id) - getImperativeOrder(b.imperative_id);
      }),
    [catalog?.imperatives]
  );

  const imperativeById = useMemo(
    () => new Map(imperatives.map((imperative) => [imperative.imperative_id, imperative])),
    [imperatives]
  );

  const orderedUnits = useMemo(() => {
    const units = [...(catalog?.units ?? [])];
    return units.sort((a, b) => {
      const orderDifference =
        getImperativeOrder(a.imperative_id) - getImperativeOrder(b.imperative_id);
      if (orderDifference !== 0) return orderDifference;
      return a.title.localeCompare(b.title);
    });
  }, [catalog?.units]);

  const activeUnit = orderedUnits.find((unit) => unit.status === 'in_progress');
  const recommendedUnit =
    orderedUnits.find((unit) => unit.unit_id === catalog?.recommended_unit_id) ??
    orderedUnits.find((unit) => unit.status === 'recommended') ??
    orderedUnits.find((unit) => unit.status === 'available') ??
    orderedUnits[0];
  const featuredUnit = activeUnit ?? recommendedUnit;
  const hasCompletedPractice = (catalog?.rounds_completed ?? 0) > 0 ||
    orderedUnits.some((unit) => unit.status === 'completed');
  const suggestionExplanation = dojoRecommendationExplanation(catalog?.recommended_reason, Boolean(activeUnit));

  if (error && !isLoading) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50/50 px-5 py-4">
        <p className="text-sm leading-6 text-red-600">Your practices could not load.</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-100 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
          >
            <RotateCcw size={13} />
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!catalog && isLoading) {
    return (
      <div className="flex min-h-56 items-center justify-center gap-2 text-sm text-how-text-secondary/60">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Laying out your practices…
      </div>
    );
  }

  if (orderedUnits.length === 0) {
    return (
      <div className="rounded-2xl border border-how-space-deep/[0.07] bg-white/60 px-5 py-5">
        <p className="text-sm leading-6 text-how-text-secondary/65">
          No practices are available yet.
        </p>
      </div>
    );
  }

  const featuredImperative = featuredUnit
    ? imperativeById.get(featuredUnit.imperative_id)
    : undefined;
  return (
    <div>
      <header className="mx-auto mb-8 max-w-[600px] text-center">
        <h1 className="font-body text-[30px] font-medium leading-9 tracking-[-0.03em] text-how-space-deep sm:text-4xl sm:leading-tight">
          Practice how you lead.
        </h1>
        <p className="mt-3 text-base leading-6 text-how-text-secondary">
          Explore a real-world scenario, reflect on the choices,
          <br />
          and try a response of your own.
        </p>
      </header>
      {featuredUnit && (
        <div className="mx-auto w-full max-w-[720px]">
        <section
          aria-labelledby="active-practice-title"
          className="mx-auto w-full max-w-[720px] rounded-2xl border border-how-space-deep/[0.07] bg-white/65 px-5 py-5 sm:px-6"
        >
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
            <div className="flex min-w-0 items-start gap-3.5">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-how-accent-warm" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase leading-4 tracking-[0.08em] text-how-text-secondary/55">
                  {activeUnit ? 'Continue your practice' : hasCompletedPractice ? 'Next for you' : 'A place to begin'}
                </p>
                <h2
                  id="active-practice-title"
                  className="mt-1 font-body text-[17px] font-medium leading-[22px] tracking-[-0.015em] text-how-space-deep"
                >
                  {getPracticeTitle(featuredImperative?.imperative_label)}
                </h2>
                <p className="mt-2 text-[13px] leading-5 text-how-text-secondary/70">
                  {featuredUnit.title}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onStartUnit(featuredUnit.unit_id)}
              disabled={disabled}
              className={cn(HOWIE_ACTION_CONTROL_CLASS, 'self-start sm:self-auto')}
            >
              {activeUnit ? 'Continue' : featuredUnit.status === 'completed' ? 'Practice again' : 'Begin'}
            </button>
          </div>
        </section>
        {suggestionExplanation && <p className={cn(SUPPORTING_COPY_CLASS, 'mt-4 px-3')}>{suggestionExplanation}</p>}
        </div>
      )}

      <section aria-labelledby="all-practices-title" className="mt-10 sm:mt-12">
          <header className="mx-auto mb-6 max-w-[720px] px-3 text-left">
            <h2 id="all-practices-title" className="font-body text-lg font-medium text-how-space-deep">All practices</h2>
            <p className="mt-2 text-sm leading-6 text-how-text-secondary/70">
              Explore in any order.
            </p>
          </header>
          <div className="mx-auto w-full max-w-[720px] space-y-8">
            {[...CURRICULUM_DIMENSIONS, null].map((dimension) => {
              const units = orderedUnits.filter((unit) => dimension
                ? unit.primary_dimension === dimension
                : !unit.primary_dimension || !CURRICULUM_DIMENSIONS.includes(unit.primary_dimension));
              if (!units.length) return null;
              return <section key={dimension ?? 'other'} aria-labelledby={`curriculum-${dimension ?? 'other'}`}>
                <h2 id={`curriculum-${dimension ?? 'other'}`} className="flex items-center gap-3 px-3 font-body text-base font-medium text-how-space-deep">
                  <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full ring-1 ring-how-space-deep/10" style={{ backgroundColor: dimension ? HOW_DIMENSION_COLOR[dimension] : 'var(--color-how-text-secondary)' }} />
                  {dimension ? HOW_DIMENSION_TITLE[dimension] : 'More practices'}
                </h2>
                <ul className="relative ml-4 mt-3 before:absolute before:inset-y-0 before:left-0 before:w-px before:-translate-x-1/2 before:bg-how-space-deep/15">
                  {units.map((unit) => <li key={unit.unit_id} className="relative pl-5 before:absolute before:left-0 before:-translate-x-1/2 before:top-9 before:h-2 before:w-2 before:rounded-full before:border before:border-how-space-deep/25 before:bg-how-paper-warm">
              <PracticeRow
                key={unit.unit_id}
                unit={unit}
                imperative={imperativeById.get(unit.imperative_id)}
                onStartUnit={onStartUnit}
                onReviewRound={onReviewRound}
                disabled={disabled}
                hideDimension
              />
                  </li>)}
                </ul>
              </section>;
            })}
          </div>
      </section>
    </div>
  );
}

export default UnitPicker;
